import { makeStyles } from "@material-ui/core/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";

import {
  Add as AddIcon,
  CloudDownload,
  Create,
  Refresh as RefreshIcon,
} from "@material-ui/icons";

import { Skeleton } from "@material-ui/lab";

import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import ToggleCell from "components/Cells/ToggleCell";
import FormUserDialog from "components/Dialogs/FormUserDialog";
import HeaderClientes from "components/_refactor/headers/clients";
import LoadingData from "components/Loading/LoadingData";
import { useVisionLogin } from "hooks/useVisionLogin";
import { useWebsocketFetch } from "hooks/useWebsocketFetch";
import Api from "lib/api/httpRequestHelper";
import { utils, writeFileXLSX } from "xlsx";
import { IsMobile } from "utils/is-mobile";
import FilterCard from "components/_refactor/filter-card";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import InputText from "components/_refactor/text/input";
import UserSummaryCards from "views/admin/components/user-summary-cards";
import { parseUserListResponse } from "views/admin/utils/parse-user-list-response";
import { buildUserListQuery } from "views/admin/utils/build-user-list-query";

import { API_ENV } from "constants/environment";

const getTableColumns = (onEditUser, loadingTable, toggleField) => [
  {
    dataField: "IdUsuario",
    text: "Id usuário",
    sort: true,
    align: "center",
    formatter: (data) => (loadingTable ? <Skeleton variant="text" /> : data),
  },
  {
    dataField: "Nome",
    text: "Nome",
    sort: true,
    formatter: (data) => (loadingTable ? <Skeleton variant="text" /> : data),
  },
  {
    dataField: "Login",
    text: "E-mail",
    sort: true,
    formatter: (data) => (loadingTable ? <Skeleton variant="text" /> : data),
  },
  {
    dataField: "Perfis",
    text: "Perfis",
    hidden: true,
  },
  {
    dataField: "UsuarioPerfis",
    text: "Perfis",
    sort: false,
    formatter: (profiles) => {
      const profilesList = profiles?.map((profile) => profile?.Nome) || [];

      return loadingTable ? (
        <Skeleton variant="text" />
      ) : (
        profilesList.join(" ")
      );
    },
  },
  {
    dataField: "Inativo",
    text: "Inativo",
    headerStyle: { width: 90 },
    formatter: (cell, row) => {
      return (
        <ToggleCell
          initialValue={row.Inativo}
          handleChange={async (params) => {
            row.Inativo = params.Inativo;

            await toggleField({ ...row, ...params }, "Inativo");
          }}
          initialLoading={loadingTable}
          field="Inativo"
        />
      );
    },
    sort: true,
  },
  {
    dataField: "Backoffice",
    text: "Backoffice",
    headerStyle: { width: 90 },
    formatter: (cell, row) => {
      return (
        <ToggleCell
          initialValue={row.Backoffice}
          handleChange={async (params) => {
            row.Backoffice = params.Backoffice;

            await toggleField({ ...row, ...params }, "Backoffice");
          }}
          initialLoading={loadingTable}
          field="Backoffice"
        />
      );
    },
    sort: true,
  },
  {
    dataField: "editar",
    text: "",
    isDummyField: true,
    formatter: (cell, row) => {
      return loadingTable ? null : (
        <Tooltip title="Editar Usuário">
          <IconButton aria-label="edit" onClick={() => onEditUser(row)}>
            <Box
              component={Create}
              width="1.25rem!important"
              height="1.25rem!important"
            />
          </IconButton>
        </Tooltip>
      );
    },
    headerStyle: { width: 45 },
  },
];

const loadingItems = [
  { IdUsuario: "0" },
  { IdUsuario: "1" },
  { IdUsuario: "2" },
  { IdUsuario: "3" },
  { IdUsuario: "4" },
  { IdUsuario: "5" },
  { IdUsuario: "6" },
  { IdUsuario: "7" },
  { IdUsuario: "8" },
  { IdUsuario: "9" },
];

function UsersTableToolbar({
  buttonRootClass,
  isMobile,
  loadingTable,
  onRefresh,
  onCreate,
  onExport,
  exportLoading,
}) {
  return (
    <Grid
      container
      className="p-4"
      style={{ padding: isMobile ? "0 24px 10px" : "0 24px" }}
    >
      <Grid item xs={12} sm={4}>
        <Box component={Typography} variant="h2" marginBottom="0!important">
          <Box component="span">Lista de Usuários</Box>
          <IconButtonLoader
            size="medium"
            extraStyle={{
              width: 24,
              height: 24,
              marginLeft: "0.3rem",
            }}
            title="Recarregar lista"
            onClick={onRefresh}
            icon={<RefreshIcon color="primary" />}
            aria-label="refresh"
            color="primary"
            disabled={loadingTable}
          />
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        sm={8}
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: isMobile ? "flex-start" : "flex-end",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <Box justifyContent="flex-end" display="flex" flexWrap="wrap">
          <Button
            variant="contained"
            classes={{
              root: buttonRootClass,
            }}
            onClick={onCreate}
            disabled={loadingTable}
          >
            <Box component={AddIcon} position="relative" top="-2px" /> Criar
          </Button>
        </Box>
        <Box
          justifyContent="flex-end"
          display="flex"
          flexWrap="wrap"
          style={{ marginLeft: isMobile ? 0 : 15 }}
        >
          <Button
            variant="contained"
            classes={{
              root: buttonRootClass,
            }}
            onClick={onExport}
            disabled={exportLoading || loadingTable}
          >
            <Box
              component={() =>
                exportLoading ? (
                  <CircularProgress
                    style={{
                      marginRight: 6,
                      width: 12,
                      height: 12,
                    }}
                  />
                ) : (
                  <CloudDownload style={{ marginRight: 6 }} />
                )
              }
              position="relative"
              top="-2px"
            />
            Exportar Usuários
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
}

function EmptyTableHeader() {
  return null;
}

const useStyles = makeStyles(componentStyles);

function Usuarios() {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const [users, setUsers] = useState(loadingItems);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [searching, setSearching] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const listFetchSeqRef = useRef(0);
  const listQueryRef = useRef({});
  const [profiles, setProfiles] = useState([]);
  const [listProfile, setListProfile] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [profileSearchLoading, setProfileSearchLoading] = useState(false);
  const profileSearchTimerRef = useRef(null);
  const lastProfileSearchRef = useRef(null);
  const profileFetchSeqRef = useRef(0);
  const [nomeOuLogin, setNomeOuLogin] = useState("");
  const [userStats, setUserStats] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(25);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const isMobile = IsMobile();
  const itemXS = isMobile ? 12 : 4;

  listQueryRef.current = {
    nomeOuLogin,
    profiles,
    currentPage,
    currentPageSize,
  };

  const toggleFormDialog = useCallback((formType) => {
    if (formType) {
      setOpenFormDialog(formType);
    }
  }, []);

  const fetchList = useCallback(
    async (page, pageSize, { showOverlay = false } = {}) => {
      const query = listQueryRef.current;
      const resolvedPage = page ?? query.currentPage;
      const resolvedSize = pageSize ?? query.currentPageSize;
      const effectiveSearch = (query.nomeOuLogin || "").trim();
      const seq = ++listFetchSeqRef.current;

      setLoadingTable(true);
      if (showOverlay) {
        setSearching(true);
      }

      try {
        const { Success, Result } = await Api.get(
          "list-user",
          "adminportal",
          buildUserListQuery({
            page: resolvedPage,
            pageSize: resolvedSize,
            nomeOuLogin: effectiveSearch,
            idPerfil: query.profiles?.length
              ? query.profiles.map((item) => item.IdPerfil)
              : undefined,
          }),
          true,
        );

        if (seq !== listFetchSeqRef.current) return;

        if (Success) {
          const {
            usuarios,
            estatisticas,
            totalRegistros: total,
            pagina,
            pageSize: size,
          } = parseUserListResponse(Result);

          setUsers(usuarios);
          setUserStats(estatisticas);
          setTotalRegistros(total);
          setCurrentPage(pagina);
          setCurrentPageSize(size);

          const searchParams = new URLSearchParams(window.location.search);
          if (searchParams.get("action") === "create-user") {
            setOpenFormDialog("create");
          }
        }
      } catch (error) {
        if (seq !== listFetchSeqRef.current) return;
        snackbar.showMessage(error, {
          severity: "error",
        });
      } finally {
        if (seq === listFetchSeqRef.current) {
          setLoadingTable(false);
          if (showOverlay) {
            setSearching(false);
          }
        }
      }
    },
    [snackbar],
  );

  const handlePageChange = useCallback(
    (page, pageSize) => {
      const nextPage = page - 1;
      const nextSize = Number(pageSize);

      if (nextPage === currentPage && nextSize === currentPageSize) {
        return;
      }

      fetchList(nextPage, nextSize);
    },
    [currentPage, currentPageSize, fetchList],
  );

  const handleSizePerPageChange = useCallback(
    (pageSize, page) => {
      const nextPage = page - 1;
      const nextSize = Number(pageSize);

      if (nextPage === currentPage && nextSize === currentPageSize) {
        return;
      }

      fetchList(nextPage, nextSize);
    },
    [currentPage, currentPageSize, fetchList],
  );

  const tablePagination = useMemo(
    () => ({
      options: [25, 50, 100],
      defaultOption: currentPageSize,
      totalSize: totalRegistros,
      showTotal: true,
      page: currentPage + 1,
      sizePerPage: currentPageSize,
      onPageChange: handlePageChange,
      onSizePerPageChange: handleSizePerPageChange,
    }),
    [
      currentPage,
      currentPageSize,
      totalRegistros,
      handlePageChange,
      handleSizePerPageChange,
    ],
  );

  const handleEditUser = async (user) => {
    setSelectedUser({ IdUsuario: user.IdUsuario });
    toggleFormDialog("edit");
    setLoadingUserDetails(true);

    try {
      const { Success, Result } = await Api.get(
        "get-user",
        "adminportal",
        `?idUsuario=${user.IdUsuario}`,
        true,
      );

      if (Success) {
        setSelectedUser(Result);
        return;
      }

      setOpenFormDialog(null);
      setSelectedUser(null);
      snackbar.showMessage("Não foi possível carregar os dados do usuário.", {
        severity: "error",
      });
    } catch (error) {
      setOpenFormDialog(null);
      setSelectedUser(null);
      snackbar.showMessage(error, {
        severity: "error",
      });
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const toggleField = useCallback(
    (row, field) => {
      return Api.postParams(
        "update-user",
        "adminportal",
        "?nome=" +
          row.Nome +
          "&idUsuario=" +
          row.IdUsuario.toString() +
          "&login=" +
          row.Login +
          "&backoffice=" +
          row.Backoffice.toString() +
          "&keycloak=" +
          (row.Keycloak ?? false).toString() +
          "&inativo=" +
          row.Inativo.toString(),
        true,
      )
        .then((res) => {
          if (res.Success) {
            if (field === "Inativo") {
              snackbar.showMessage(
                `Usuário ${res.Result.IdUsuario.toString()} foi ${
                  res.Result.Inativo ? "inativado" : "ativado"
                } com sucesso!`,
                {
                  severity: "success",
                },
              );
              fetchList(currentPage, currentPageSize);
            } else if (field === "Backoffice") {
              snackbar.showMessage(
                `Backoffice do Usuário ${res.Result.IdUsuario.toString()} foi ${
                  res.Result.Backoffice ? "ativado" : "desativado"
                } com sucesso!`,
                {
                  severity: "success",
                },
              );
            }
          } else {
            snackbar.showMessage(
              `Ocorreu um erro ao atualizar informação do Usuário ${row.IdUsuario.toString()}. Verifique os parâmetros informados.`,
              {
                severity: "error",
              },
            );
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });
        });
    },
    [snackbar, fetchList, currentPage, currentPageSize],
  );

  const updateList = async (updatedProfile) => {
    const newProfileList = users?.map((user) => {
      if (user.IdUsuario === updatedProfile.IdUsuario) return updatedProfile;
      else return user;
    });

    setUsers(newProfileList);
  };

  const fetchProfiles = useCallback(
    async (nome = "", { searching = false } = {}) => {
      const trimmedNome = (nome || "").trim();
      const seq = ++profileFetchSeqRef.current;

      if (searching) {
        setProfileSearchLoading(true);
      } else {
        setLoadingProfiles(true);
      }

      const limit = trimmedNome ? 50 : 20;
      const query =
        `?situacaoPerfil=ativos&limit=${limit}` +
        (trimmedNome ? `&nome=${encodeURIComponent(trimmedNome)}` : "");

      try {
        const { Success, Result } = await Api.get(
          "list-profile",
          "adminportal",
          query,
          true,
        );

        if (seq !== profileFetchSeqRef.current) return;

        if (Success) {
          setListProfile(Array.isArray(Result) ? Result : []);
          setUsers(Result?.Usuarios ?? []);
          setSearching(false);
        }
      } catch (error) {
        if (seq !== profileFetchSeqRef.current) return;
        snackbar.showMessage(error, {
          severity: "error",
        });
      } finally {
        if (seq === profileFetchSeqRef.current) {
          if (searching) {
            setProfileSearchLoading(false);
          } else {
            setLoadingProfiles(false);
          }
        }
      }
    },
    [snackbar],
  );

  const handleProfileInputChange = useCallback(
    (_, inputValue, reason) => {
      if (reason !== "input" && reason !== "clear") return;

      const searchText = (inputValue || "").trim();

      if (profileSearchTimerRef.current) {
        clearTimeout(profileSearchTimerRef.current);
      }

      profileSearchTimerRef.current = setTimeout(() => {
        if (lastProfileSearchRef.current === searchText) return;

        lastProfileSearchRef.current = searchText;
        fetchProfiles(searchText, { searching: true });
        profileSearchTimerRef.current = null;
      }, 400);
    },
    [fetchProfiles],
  );

  const profileOptions = useMemo(() => {
    const merged = new Map();

    [...profiles, ...listProfile].forEach((profile) => {
      if (profile?.IdPerfil != null) {
        merged.set(profile.IdPerfil, profile);
      }
    });

    return Array.from(merged.values());
  }, [profiles, listProfile]);

  const handleRefresh = async () => {
    return fetchList(currentPage, currentPageSize);
  };

  const handleSearch = () => {
    fetchList(0, currentPageSize, { showOverlay: true });
  };

  const handleClearFilters = () => {
    setProfiles([]);
    setNomeOuLogin("");
    lastProfileSearchRef.current = null;
    fetchProfiles();
    fetchList(0, currentPageSize, { showOverlay: true });
  };

  useEffect(() => {
    fetchList(0, 25, { showOverlay: true });
    fetchProfiles();

    return () => {
      if (profileSearchTimerRef.current) {
        clearTimeout(profileSearchTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { userId } = useVisionLogin();

  const {
    handleFetchData: handleFetchUsersToExport,
    status: wsStatus,
    handleFetchUrl: connectToUserExportWebsocket,
    disconnect: disconnectUserExportWebsocket,
  } = useWebsocketFetch({
    onSuccess: (data) => {
      disconnectUserExportWebsocket();

      const json = data.reduce((sheet, userToExport) => {
        return [
          ...sheet,
          ...userToExport.UsuarioLoja.map((store) => ({
            Login: userToExport.Login,
            Perfis: userToExport.UsuarioPerfis.map(
              (perfil) => perfil.Nome,
            ).join(", "),
            CNPJ: store.CnpjCpfPessoa,
            "Razão Social": store.Apelido,
          })),
          {
            Login: "",
            Perfis: "",
            CNPJ: "",
            "Razão Social": "",
          },
        ];
      }, []);

      const receipt = utils.json_to_sheet(json);

      const wb = utils.book_new();
      utils.book_append_sheet(wb, receipt, "Vinculo Usuário-Lojas");
      writeFileXLSX(wb, "Vinculo Usuário-Lojas.xlsx");
    },
  });

  const handleSendMessage = () => {
    connectToUserExportWebsocket(
      `wss://cw9qfq2tvl.execute-api.us-east-1.amazonaws.com/${API_ENV}`,
    );
    handleFetchUsersToExport({
      action: "user-list",
      params: {
        querystring: {
          idPerfil: "",
          nome: "",
          email: "",
          status: "",
        },
        header: {
          "K-IdUser": userId,
        },
      },
    });
    snackbar.showMessage("Aguarde exportação dos usuários.", {
      severity: "info",
      autoHideDuration: 60000,
    });
  };

  const tableColumns = useMemo(
    () => getTableColumns(handleEditUser, loadingTable, toggleField),
    [handleEditUser, loadingTable, toggleField],
  );

  return (
    <>
      <HeaderClientes section="Usuários" subsection="Administração" />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <UserSummaryCards
          stats={userStats}
          loading={searching && loadingTable}
        />

        <FilterCard
          handleSearch={handleSearch}
          disabled={searching}
          searching={searching}
          handleClearFilters={handleClearFilters}
        >
          <Grid item xs={itemXS}>
            <AutoCompleteField
              options={profileOptions}
              label="Perfil"
              onChange={setProfiles}
              onInputChange={handleProfileInputChange}
              optionIdKey="IdPerfil"
              optionLabelKey="Nome"
              value={profiles}
              disabled={searching}
              loading={loadingProfiles}
              optionsSearchLoading={profileSearchLoading}
              preserveInputWhenLoading
              disableClientFilter
              multiple
            />
          </Grid>

          <Grid item xs={itemXS}>
            <InputText
              label="Nome ou Login"
              value={nomeOuLogin}
              onChange={(event) => setNomeOuLogin(event.target.value)}
              disabled={searching}
              props={{
                onKeyPress: (event) => {
                  if (event.code === "Enter") {
                    handleSearch();
                  }
                },
              }}
            />
          </Grid>
        </FilterCard>

        <Grid container>
          <Grid item xs={12}>
            <LoadingData
              searching={searching}
              onClick={() => setSearching(false)}
              classes={classes.root}
              label="Buscando dados..."
            />

            <Card classes={{ root: classes.cardRoot }}>
              <UsersTableToolbar
                buttonRootClass={classes.buttonRootWhite}
                isMobile={isMobile}
                loadingTable={loadingTable}
                onRefresh={handleRefresh}
                onCreate={() => toggleFormDialog("create")}
                onExport={handleSendMessage}
                exportLoading={wsStatus === "fetching"}
              />
              <ToolkitTable
                keyField="IdUsuario"
                dataTable={loadingTable ? loadingItems : users}
                columnModel={tableColumns}
                allowEmpty
                CustomHeader={EmptyTableHeader}
                pagination={tablePagination}
                bootstrapTableProps={{
                  remote: true,
                  onTableChange: () => {},
                }}
              />
            </Card>
          </Grid>
        </Grid>
      </Container>

      {Boolean(openFormDialog) && (
        <FormUserDialog
          open
          formType={openFormDialog}
          onClose={(user) => {
            setOpenFormDialog(null);
            setSelectedUser(null);
            setLoadingUserDetails(false);
            if (user?.IdUsuario > 0) {
              if (openFormDialog === "create") {
                const newProfileList = [...users, { ...user }];

                setUsers(newProfileList);
                fetchList(currentPage, currentPageSize);
              } else {
                updateList(user);
              }
            }
          }}
          selectedUser={selectedUser}
          loadingDetails={loadingUserDetails}
        />
      )}
    </>
  );
}

Usuarios.propTypes = {};

export default Usuarios;
